import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileWarning, PenLine, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, dataOraIt, formatError } from "@/lib/api";

const Riga = ({ testId, titolo, sotto, children }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 text-sm" data-testid={testId}>
    <div className="min-w-0"><p className="truncate font-semibold text-slate-900">{titolo}</p><p className="truncate text-xs text-slate-500">{sotto}</p></div>
    <div className="flex gap-1.5">{children}</div>
  </li>
);

export const DashboardDaFirmare = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("privacy");
  const load = useCallback(() => api.get("/da-firmare").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const carta = async (n) => {
    try {
      await api.post(`/noleggi/${n.id}/firma-carta`, { firmato: true });
      toast.success(`Contratto ${n.numero} segnato come firmato su carta`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (!d) return null;
  const tot = d.privacy_totale + d.contratti_totale;
  const lista = tab === "privacy" ? d.privacy : d.contratti;
  const resto = (tab === "privacy" ? d.privacy_totale : d.contratti_totale) - lista.length;

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="dashboard-da-firmare">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold">
          <FileWarning className={`h-4 w-4 ${tot ? "text-rose-600" : "text-emerald-600"}`} /> Da firmare
          <span className="rounded-sm bg-slate-100 px-1.5 font-mono text-xs" data-testid="da-firmare-totale">{tot}</span>
        </h2>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-8">
            <TabsTrigger value="privacy" className="text-xs" data-testid="da-firmare-tab-privacy">Privacy ({d.privacy_totale})</TabsTrigger>
            <TabsTrigger value="contratti" className="text-xs" data-testid="da-firmare-tab-contratti">Contratti ({d.contratti_totale})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
        {tab === "privacy" && d.privacy.map((c) => (
          <Riga key={c.id} testId={`da-firmare-privacy-${c.id}`} titolo={c.nome} sotto={c.telefono || "Nessun telefono"}>
            <Button size="sm" variant="outline" onClick={() => navigate(`/clienti/${c.id}/privacy`)} data-testid={`da-firmare-privacy-firma-${c.id}`}><PenLine className="mr-1 h-3.5 w-3.5" /> Firma</Button>
          </Riga>
        ))}
        {tab === "contratti" && d.contratti.map((n) => (
          <Riga key={n.id} testId={`da-firmare-contratto-${n.id}`} titolo={`${n.numero} · ${n.cliente_nome}`} sotto={`${n.bici_desc || "—"} · ritiro ${dataOraIt(n.inizio)}`}>
            <Button size="sm" variant="outline" onClick={() => navigate(`/noleggi/${n.id}/contratto`)} data-testid={`da-firmare-contratto-firma-${n.id}`}><PenLine className="mr-1 h-3.5 w-3.5" /> Firma</Button>
            <Button size="sm" variant="ghost" className="text-slate-500" title="Firmato su carta" onClick={() => carta(n)} data-testid={`da-firmare-contratto-carta-${n.id}`}><FileCheck2 className="mr-1 h-3.5 w-3.5" /> Su carta</Button>
          </Riga>
        ))}
        {lista.length === 0 && <li className="px-5 py-6 text-center text-sm text-emerald-700" data-testid="da-firmare-empty">Tutto firmato, ottimo lavoro!</li>}
        {resto > 0 && <li className="px-5 py-2 text-center text-xs text-slate-400">e altri {resto}...</li>}
      </ul>
    </section>
  );
};
